import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { FormApi } from 'final-form';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Loader } from 'ncoded-component-library';
import PasswordField from 'components/PasswordField';
import validate from 'validations';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import AccountActions from './components/AccountActions';
import FloatingActions from 'components/FloatingActions';
import { ProfileForm, ProfileFormValues } from '../../hooks/useProfileForm';

import './Account.styles.scss';

type AccountProps = {
  className?: string;
};

const INITIAL_PASSWORD_VALUES = {
  oldPassword: '',
  newPassword: '',
} as ProfileFormValues;

const AccountContent: React.FC<AccountProps> = (props) => {
  const { className } = props;

  const classes = classNames('account', className);

  const { t } = useTranslation();

  return (
    <div className={classes}>
      <section className="account__password profile-card-container">
        <div className="account__password__parahraphs">
          <p className="profile-section-title">{t('password')}</p>
          <p className="description">{t('passwordSectionDescription')}</p>
        </div>

        <div className="account__password__fields">
          <Field
            name="oldPassword"
            label={t('oldPassword')}
            placeholder={t('enterYourCurrentPassword')}
            component={PasswordField}
            withoutStrengthIndicator
            validate={validate.passwordFieldsValidation()}
          />
          <Field
            name="newPassword"
            label={t('newPassword')}
            placeholder={t('enterYourNewPassword')}
            component={PasswordField}
            validate={validate.passwordFieldsValidation()}
          />
        </div>
      </section>

      <section className="account__actions profile-card-container">
        <p className="profile-section-title">{t('accountActions')}</p>
        <AccountActions />
      </section>
    </div>
  );
};

const Account: React.FC<AccountProps> = (props) => {
  const { className } = props;

  const { changePassword } = useContext(CurrentUserContext);

  const { t } = useTranslation();

  const onSubmit = useCallback(
    async (values: ProfileFormValues, form: FormApi<ProfileFormValues>) => {
      const { oldPassword, newPassword } = values;

      await changePassword?.(
        { oldPassword, newPassword },
        {
          onSuccess: () => form.reset(),
        },
      );
    },
    [changePassword],
  );

  return (
    <ProfileForm
      initialValues={INITIAL_PASSWORD_VALUES}
      onSubmit={onSubmit}
      render={({ handleSubmit, submitting, values, errors }) => {
        const { oldPassword, newPassword } = values || {};

        const isValidChangePassword =
          !errors.newPassword &&
          oldPassword &&
          newPassword &&
          oldPassword !== newPassword;

        return (
          <form onSubmit={handleSubmit} className="profile-form">
            {submitting && <Loader className="fixed without-header" />}

            <AccountContent className={className} />

            <FloatingActions
              submitTxt={t('changePassword')}
              message={t('unsavedChanges')}
              isVisible={isValidChangePassword}
              disabled={submitting || !isValidChangePassword}
            />
          </form>
        );
      }}
    />
  );
};

export default Account;
